import React from 'react';
import { Badge } from './ui/badge.tsx';
import { isUnreleasedClient } from '../constants/csvMerge';
import { isParaTrialClient } from '../utils/allocationVisibility';

/** Inline badge for paraplanner-created test clients (`isTestClient` on client doc). */
export function TestClientBadge({ className = '' }) {
  return (
    <Badge
      variant="secondary"
      className={`text-[10px] px-1.5 py-0 shrink-0 border-transparent bg-zinc-700 text-zinc-100 hover:bg-zinc-700 dark:bg-zinc-600 dark:text-zinc-50 dark:hover:bg-zinc-600 ${className}`.trim()}
    >
      Test
    </Badge>
  );
}

/** Inline badge for DNQ clients (`isDnq` on client doc). */
export function DnqBadge({ className = '' }) {
  return (
    <Badge
      variant="secondary"
      className={`text-[10px] px-1.5 py-0 shrink-0 border-transparent bg-red-600 text-white hover:bg-red-600 dark:bg-red-700 dark:text-red-50 dark:hover:bg-red-700 ${className}`.trim()}
    >
      DNQ
    </Badge>
  );
}

/** Inline badge for Para trial clients (`assignment.allocation.mode: 'trial'`, not sent back). */
export function ParaTrialBadge({ className = '' }) {
  return (
    <Badge
      variant="secondary"
      title="Para trial client: prepared in Para, not by the paraplanners"
      className={`text-[10px] px-1.5 py-0 shrink-0 border-transparent bg-violet-600 text-white hover:bg-violet-600 dark:bg-violet-700 dark:text-violet-50 dark:hover:bg-violet-700 ${className}`.trim()}
    >
      Para
    </Badge>
  );
}

/** Inline badge for archived clients (`isArchived` on client doc) — search surfaces them outside the Archive bucket. */
export function ArchivedBadge({ className = '' }) {
  return (
    <Badge
      variant="secondary"
      className={`text-[10px] px-1.5 py-0 shrink-0 border-border bg-muted text-muted-foreground hover:bg-muted ${className}`.trim()}
    >
      Archived
    </Badge>
  );
}

/** Inline badge for unreleased bucketed clients (manual AR entry / CSV merge). */
export function UnreleasedBadge({ className = '' }) {
  return (
    <Badge
      variant="secondary"
      className={`text-[10px] px-1.5 py-0 shrink-0 border-transparent bg-amber-500 text-white hover:bg-amber-500 dark:bg-amber-600 dark:text-amber-50 dark:hover:bg-amber-600 ${className}`.trim()}
    >
      Unreleased
    </Badge>
  );
}

/** Inline badge shown when an unreleased client's Adviser Logic file notes have merged. */
export function NotesMergedBadge({ className = '' }) {
  return (
    <Badge
      variant="secondary"
      className={`text-[10px] px-1.5 py-0 shrink-0 border-transparent bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-700 dark:text-emerald-50 dark:hover:bg-emerald-700 ${className}`.trim()}
    >
      Notes ✓
    </Badge>
  );
}

/**
 * Client name with optional test/DNQ/Para/archived/unreleased badges — use in portal client lists and headers.
 */
export default function ClientNameWithBadge({
  client,
  name,
  nameClassName = 'text-sm font-medium text-foreground truncate',
  blockEllipsis = false,
}) {
  const nameClasses = blockEllipsis
    ? `${nameClassName} block overflow-hidden text-ellipsis`
    : nameClassName;

  const badges = [
    client?.isTestClient && <TestClientBadge key="test" />,
    client?.isDnq === true && <DnqBadge key="dnq" />,
    isParaTrialClient(client) && <ParaTrialBadge key="para" />,
    client?.isArchived === true && <ArchivedBadge key="archived" />,
    isUnreleasedClient(client) && <UnreleasedBadge key="unreleased" />,
    isUnreleasedClient(client) && client?.fileNotesMerge?.mergedAt && <NotesMergedBadge key="notes" />,
  ].filter(Boolean);

  return (
    <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
      <span className={nameClasses}>{name}</span>
      {/* In a narrow column the badges give up space before the name does. */}
      {badges.length > 0 && (
        <span className="inline-flex items-center gap-1.5 min-w-0 shrink-[3] overflow-hidden">{badges}</span>
      )}
    </span>
  );
}
